import { ENTER } from '@wordpress/keycodes';
import {
  ColumnApi,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';
import _, { values } from 'lodash';
import { indexBy, pipe } from 'ramda';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { InferredProgressValue } from 'api/project';
import { Task as ITaskApi, TaskAPI } from 'api/tasks';
import { Column, SortState } from 'components/atoms/grid';
import { Filter } from 'components/molecules/filters/types';
import { FilterGroupType } from 'components/pages/types';
import { EXTRA_DETAILS, PermissionRuleName } from 'constant';
import { taskNormalizer } from 'data/tasks/schema';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { Transformer as HashTransformer, useHash } from 'hooks/use-hash';
import { Translate } from 'hooks/use-translation';
import {
  calculateProgressPercent,
  getFieldMapReducer as getProjectFieldMapReducer,
  lastStatusUpdatedAt,
} from 'routes/projects/list-v2/transformer';
import { logger } from 'services';
import {
  CreatableContent,
  CreatableEditableRefreshableContent,
  Loaded,
  Loading,
} from 'stores/core';
import { ProjectField, TaskField } from 'stores/fields';
import { loadedAllTasks, Task } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { toStoreTask } from 'utils/to-store-task';
import { ColumnOrder } from '../grid-custom-column';
import { QueryCombinerType } from './combiner';
import { getComparator } from './comparators';
import { getEditor } from './editors';
import { isSelect } from './field-value';
import { FieldType, FilterableField, SourceType } from './fields';
import { FieldFilter, useFilterEffect } from './filters';
import { GroupCriteria, GroupedFilter } from './grouped-filter-popover';
import { getHeaderTemplate, getRenderer, getValueFormatter } from './renderers';
import { useAllTasksPaginatedSocketUpdates } from './use-all-tasks-paginated-socket-updates';
import { getValueGetter } from './value-getter';

export const defaultGroupedFilter = (
  defaultGroup = FilterGroupType.Task,
): GroupedFilter => ({
  type: QueryCombinerType.All,
  defaultGroup,
  groupCriteria: [],
});

export const isGroupFilter = (
  filter: GroupedFilter | Filter,
): filter is GroupedFilter => {
  return (<GroupedFilter>filter).groupCriteria != null;
};

export const convertGroupCriteriaToNonGroupFilter = ({
  criteria: groupCriteria,
  type,
}: {
  criteria: GroupCriteria[];
  type: QueryCombinerType;
}): Filter => {
  return {
    criteria: groupCriteria.map(({ criteria }) => criteria),
    type,
  };
};

// TODO: Move to backend?!
export const default_column_order = [
  'checkbox',
  'status',
  'project',
  'projectPhaseId',
  'assignee',
  'status',
  'priority',
  'startDate',
  'dueDate',
  'effort',
  'timeEntryMinutes',
  'progress',
  'atRisk',
  'completedAt',
  'responsible',
  'createdBy',
  'createdAt',
  'jiraIntegration',
];
export const stringToColumnOrder = (colId: string): ColumnOrder => ({
  colId,
  hide: false,
  sort: null,
  sortIndex: null,
  pinned: ['checkbox', 'status'].includes(colId) ? 'left' : undefined,
  width: colId === 'checkbox' ? 40 : colId === 'status' ? 400 : 140,
});
export const DEFAULT_COLUMN_ORDER: ColumnOrder[] =
  default_column_order.map(stringToColumnOrder);
export const excluded_fields = ['status', 'taskName', 'overdue', 'type'];
export const time_tracked_fields = ['timeEntryMinutes'];
export const readonly_fields = [
  'completedAt',
  'createdBy',
  'createdAt',
  'atRisk',
  'project',
  'responsible',
  'jiraIntegration',
  'timeEntryMinutes',
];

export const suppressEnterKey = ({
  editing,
  event,
}: SuppressKeyboardEventParams): boolean => {
  if (editing && event.keyCode === ENTER) {
    return true;
  }

  return false;
};

export const toRows = (
  task_ids: number[],
  task_lookup: { [id: number]: CreatableEditableRefreshableContent<Task> },
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  task_ids.forEach((id) => {
    const lookup = task_lookup[id];

    if (!lookup) {
      return;
    }
    const [status, task] = lookup;

    if (!task) {
      return;
    }

    if (
      status === 'creating' ||
      status === 'loaded' ||
      status === 'updating' ||
      status === 'refreshing'
    ) {
      rows.push({ ...task });
    }
  });

  return rows;
};

const columnsTransformer: HashTransformer<{ show: string[] }> = {
  from: (parsed) => {
    const [show] =
      typeof parsed?.show === 'string' ? [parsed.show] : parsed?.show ?? [];

    return { show: show?.split('~') ?? [] };
  },
  to: ({ show }) => ({ show }),
};

export const useColumns = (
  lookup: Record<number, TaskField>,
  translate: Translate,
): Column[] => {
  const [view] = useHash(columnsTransformer);
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const updated_readonly_fields = useMemo(
    () =>
      isAllowedForCurrentUser(PermissionRuleName.ProjectPhaseAddTasksAction)
        ? readonly_fields
        : readonly_fields.concat(['projectPhaseId']),
    [isAllowedForCurrentUser],
  );

  return values(lookup)
    .filter((f) => !excluded_fields.includes(f.fieldName))
    .filter((f) =>
      isTimesheetFeatureEnabled
        ? true
        : !time_tracked_fields.includes(f.fieldName),
    )
    .filter((f) =>
      view?.show?.length && f.fieldLabel
        ? view.show.includes(f.fieldLabel)
        : true,
    )
    .map(({ ...field }) => ({
      ...field,
      editable: !updated_readonly_fields.includes(field.fieldName),
    }))
    .map(({ editable, ...field }) => ({
      name: field.fieldName,
      headerName: field.fieldLabel,
      editable,
      valueGetter: getValueGetter(field),
      valueFormatter: getValueFormatter(field),
      comparator: getComparator(field),
      renderer: getRenderer(field),
      editor: getEditor(field),
      suppressKeyboardEvent:
        isSelect(field.type) ||
        field.type === FieldType.Date ||
        field.type === FieldType.MultiLineText
          ? suppressEnterKey
          : undefined,
      headerTemplate: getHeaderTemplate({ field, editable, translate }),
      width: field.type === FieldType.MultiLineText ? 300 : 140,
    }));
};

type FieldId = number;
interface ConstructNormalizedTaskParams {
  taskList?: ITaskApi[];
  fieldList: Record<FieldId, TaskField>;
  isLoading: boolean;
}

type TaskId = number;

export const constructNormalizedTask = ({
  taskList,
  fieldList,
  isLoading,
}: ConstructNormalizedTaskParams): {
  byId: Record<TaskId, CreatableContent<Task>>;
  ids: Loading | Loaded<TaskId[]>;
} => {
  if (!taskList) {
    return { ids: Loading(), byId: {} };
  }
  const updatedTaskList: ITaskApi[] = taskList.map((task) => ({
    ...task,
    fields: task.fields.map(withValidCustomFieldsForData),
  }));
  const {
    entities: { tasks },
  } = taskNormalizer(updatedTaskList);
  const to_row = toStoreTask(fieldList, tasks);

  const { payload: allMyTask } = loadedAllTasks(values(tasks).map(to_row));

  return {
    byId: pipe(
      () => allMyTask.map(Loaded),
      indexBy((x) => x[1].taskId),
    )(),
    ids: !isLoading
      ? Loaded(updatedTaskList?.map((task) => task.taskId))
      : Loading(),
  };
};

const transformProjectFields = (
  projectField: Record<FieldId, ProjectField>,
  tasks?: ITaskApi[],
) => {
  const toFieldMap = getProjectFieldMapReducer(projectField);

  return tasks?.map(({ project }) => {
    const { fields } = project;
    const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
    const progressPercent = calculateProgressPercent(
      project.progressStatus,
      project.totalTaskCount,
    );

    return {
      ...project,
      ...fieldsMap,
      currentPhases:
        project.currentPhases && project.currentPhases.length > 0
          ? project.currentPhases
          : null,
      progressPercent,
      meta: {
        customer: project.customer,
      },
      arr: project.annualizedRecurringRevenue,
      customer: project.customer?.companyId,
      projectOwner: project.projectOwner?.userId,
      inferredProgress:
        project.inferredProgress?.value === InferredProgressValue.None
          ? undefined
          : project.inferredProgress?.value,
      estimatedDueDate: project.inferredProgressDays,
      teamMembers: (project.teamMembers ?? []).map(({ userId }) => userId),
      projectHours: {
        budget_hours: project.budgetedHours,
        total_minutes: project.totalMinutes,
        project_name: project.projectName,
      },
      lastStatusUpdate: lastStatusUpdatedAt(project.pastProjectStatus ?? []),
    };
  });
};
const getSortAsString = (
  lookup: Record<string, FilterableField>,
  sortOrder?: SortState[],
): string => {
  const [sortedField] = sortOrder || [];
  let sortColName = '';

  switch (sortedField?.colId) {
    case 'status':
      sortColName = 'taskName';
      break;
    case 'projectPhaseId':
      sortColName = 'phaseName';

      return `${sortColName}.${sortedField.sort}`;
    default:
      sortColName = sortedField?.colId;
  }

  if (!sortedField) {
    return '';
  }
  const field = lookup[sortColName];

  if (!field) {
    return '';
  }
  if (field.userCreated) {
    return `field.${field.fieldId}.${sortedField.sort}`;
  }

  return `${field.fieldName}.${sortedField.sort}`;
};

export const useServerSideDataSource = (
  taskFields: Record<FieldId, TaskField>,
  projectFields: Record<FieldId, ProjectField>,
  filter: GroupedFilter,
  lookup: Record<string, FilterableField>,
): {
  onGridReady: (event: GridReadyEvent) => void;
  gridApi: GridApi | undefined;
  isInitialLoading: boolean;
  gridColumnApi: ColumnApi | undefined;
} => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
    isEnabled: true,
  });
  // Injecting external filter to ServerSideDataSource is very difficult. Have to find a way
  const filterRef = useRef(filter);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  useFilterEffect(() => {
    setIsInitialLoading(true);
  });

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApi(event.api);
      setGridColumnApi(event.columnApi);
      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, successCallback, failCallback } = params;
          const { endRow, startRow, sortModel } = request;
          const validFilter = filterRef.current.groupCriteria.filter((x) =>
            FieldFilter.isValid(x.criteria),
          );

          TaskAPI.getPaginatedTaskList({
            startRow,
            endRow,
            filter: validFilter,
            filterType: filterRef.current.type,
            sort: getSortAsString(lookup, sortModel),
          })
            .then(({ count, data }) => {
              setIsInitialLoading(false);
              const { byId: taskLookup, ids: allMyIds } =
                constructNormalizedTask({
                  fieldList: taskFields,
                  isLoading: false,
                  taskList: data,
                });

              if (allMyIds[0] === 'loading') {
                return;
              }
              const tasks = toRows(allMyIds[1], taskLookup);

              const projectIds = data.map((t) => t.project.projectId);

              socketSubscriber(_.uniq(projectIds));
              const projects = transformProjectFields(projectFields, data);
              const taskWithProjects = tasks.map((task) => ({
                ...task,
                [EXTRA_DETAILS]: {
                  [SourceType.Project]: projects?.find(
                    ({ projectId }) => projectId === task.project,
                  ),
                },
              }));

              successCallback(taskWithProjects, count);
            })
            .catch((e) => {
              logger.log('error downloading content', e);
              setIsInitialLoading(false);
              failCallback();
            });
        },
      });
    },
    [lookup, taskFields, socketSubscriber, projectFields],
  );

  return {
    onGridReady,
    gridApi,
    isInitialLoading,
    gridColumnApi,
  };
};

export const useServerSideDataSourceForDashBoard = (
  taskFields: Record<FieldId, TaskField>,
  projectFields: Record<FieldId, ProjectField>,
  dashboardFilter: SimpleSearchFilter,
  widgetFilter: SimpleSearchFilter,
  lookup: Record<string, FilterableField>,
  widgetId: number,
): {
  onGridReady: (event: GridReadyEvent) => void;
  gridApi: GridApi | undefined;
  isInitialLoading: boolean;
  gridColumnApi: ColumnApi | undefined;
} => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
  const socketSubscriber = useAllTasksPaginatedSocketUpdates({
    fields: taskFields,
    gridApi,
    isEnabled: true,
  });

  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setIsInitialLoading(true);
      setGridApi(event.api);
      setGridColumnApi(event.columnApi);
      event.api.setServerSideDatasource({
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, successCallback, failCallback } = params;
          const { endRow, startRow, sortModel } = request;

          TaskAPI.getPaginatedTaskListForDashbaord({
            dashboardFilter,
            widgetFilter,
            startRow,
            endRow,
            widgetId,
            sort: getSortAsString(lookup, sortModel),
          })
            .then(({ count, data }) => {
              setIsInitialLoading(false);
              const { byId: taskLookup, ids: allMyIds } =
                constructNormalizedTask({
                  fieldList: taskFields,
                  isLoading: false,
                  taskList: data,
                });

              if (allMyIds[0] === 'loading') {
                return;
              }
              const tasks = toRows(allMyIds[1], taskLookup);

              const projectIds = data.map((t) => t.project.projectId);

              socketSubscriber(_.uniq(projectIds));
              const projects = transformProjectFields(projectFields, data);
              const taskWithProjects = tasks.map((task) => ({
                ...task,
                [EXTRA_DETAILS]: {
                  [SourceType.Project]: projects?.find(
                    ({ projectId }) => projectId === task.project,
                  ),
                },
              }));

              successCallback(taskWithProjects, count);
            })
            .catch((e) => {
              logger.log('error downloading content', e);
              setIsInitialLoading(false);
              failCallback();
            });
        },
      });
    },
    [
      dashboardFilter,
      widgetFilter,
      widgetId,
      lookup,
      taskFields,
      socketSubscriber,
      projectFields,
    ],
  );

  return {
    onGridReady,
    gridApi,
    isInitialLoading,
    gridColumnApi,
  };
};

export const toProjectColumns = (
  lookup: Record<number, ProjectField>,
  translate: Translate,
  includedProjectColumns: string[],
): Column[] =>
  values(lookup)
    .filter((f) => includedProjectColumns.includes(f.fieldName))
    .map((field) => ({
      name: `project-${field.fieldName}`,
      headerName: field.fieldLabel,
      editable: false,
      valueGetter: getValueGetter(field),
      valueFormatter: getValueFormatter(field),
      comparator: getComparator(field),
      renderer: getRenderer(field),
      suppressKeyboardEvent:
        isSelect(field.type) ||
        field.type === FieldType.Date ||
        field.type === FieldType.MultiLineText
          ? suppressEnterKey
          : undefined,
      headerTemplate: getHeaderTemplate({
        field,
        editable: false,
        translate,
        isProjectHeader: true,
      }),
      width: field.type === FieldType.MultiLineText ? 300 : 140,
    }));
